import { useEffect, useRef, useState } from 'react';
import type { ControleDoMapa, Falha, Feicao, LinhaDoMapa } from '../../mapaGeotecnico';
import { aoMudarTemaEfetivo, temaEfetivo } from '../../tema';
import { Aviso } from '../../ui';

/**
 * O mapa da aba Análise integrada — onda 316 (R8): as verticais da campanha no Mapa Geotécnico (o motor de sempre,
 * sob demanda: o MapLibre só desce quando a aba abre) e as linhas de corte por cima — a provisória tracejada, as
 * gravadas contínuas, com os rótulos A/B (`definirLinhas`).
 *
 * Com `tracar`, clicar numa vertical põe o vértice NELA (o clique chega por `aoEscolher`, com o lugar = o id da
 * vertical) e clicar fora põe um vértice livre («a linha pode passar por qualquer ponto», `aoClicarNoMapa`). Sem
 * WebGL, o mapa diz por quê, e o caminho é «De … Até …» embaixo — o mesmo do teclado.
 */

/** As escolhas do mapa da análise ficam no aparelho, nestas chaves (à parte das do Mapa Geotécnico). */
const CHAVE_DO_FUNDO = 'labmanager.cptu.analise.fundo';
const CHAVE_DAS_CAMADAS = 'labmanager.cptu.analise.camadas';
/** O lado do ícone, como no mapa do Hub. */
const TAMANHO_DO_ICONE = 30;
/** Blumenau, se nenhuma vertical tivesse coordenada (a aba não abre o mapa nesse caso). */
const CASA: [number, number] = [-26.9194, -49.0661];

/** O texto que vai a uma dica HTML do mapa, escapado. */
const escapar = (s: string) => s.replace(/[&<>"']/g, (x) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[x]!);

/** Uma vertical no mapa: o id (o lugar), o código e a coordenada. */
export interface VerticalNoMapa { id: string; codigo: string; latitude: number; longitude: number }

/** Um vértice da linha: a coordenada e, quando caiu numa vertical, o código dela. */
export interface VerticeDaLinha { latitude: number; longitude: number; codigo: string | null }

export function MapaDaCampanha({ verticais, linhas, cliente, tracar, aoVertice, aoSemMapa }: {
  verticais: readonly VerticalNoMapa[];
  linhas: readonly LinhaDoMapa[];
  cliente: string;
  /** Os cliques põem vértices (o computador, com a linha em traço). */
  tracar: boolean;
  aoVertice(v: VerticeDaLinha): void;
  /** O mapa não desenha (sem WebGL, o motor que não carregou): a tela mostra o «De … Até …». */
  aoSemMapa?(semMapa: boolean): void;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const [controle, setControle] = useState<ControleDoMapa | null>(null);
  const [falhas, setFalhas] = useState<Set<Falha>>(new Set());
  const [semMotor, setSemMotor] = useState(false);
  // O clique chega ao mapa por funções guardadas nele: a ref leva sempre as propriedades da vez.
  const atual = useRef({ verticais, tracar, aoVertice });
  atual.current = { verticais, tracar, aoVertice };

  useEffect(() => {
    let vivo = true;
    let criado: ControleDoMapa | null = null;
    let desligarTema: (() => void) | null = null;
    void (async () => {
      const { criarMapa } = await import('../../mapaGeotecnico');
      if (!vivo || !caixa.current) return;
      const m = await criarMapa(caixa.current, {
        casa: CASA,
        chaveDoFundo: CHAVE_DO_FUNDO,
        chaveDasCamadas: CHAVE_DAS_CAMADAS,
        tema: temaEfetivo(),
        tamanhoDoIcone: TAMANHO_DO_ICONE,
        dica: (f) => `<strong>${escapar(f.nome)}</strong>`,
        textoDaLista: (f) => `Vertical ${f.nome}`,
      });
      if (!vivo) { m.destruir(); return; }
      criado = m;
      m.aoFalhar((f, falhou) => setFalhas((antes) => {
        if (antes.has(f) === falhou) return antes;
        const novo = new Set(antes);
        if (falhou) novo.add(f); else novo.delete(f);
        return novo;
      }));
      m.aoEscolher((lugar) => {
        const { verticais: vs, tracar: t, aoVertice: fn } = atual.current;
        const v = vs.find((x) => x.id === lugar);
        if (t && v) fn({ latitude: v.latitude, longitude: v.longitude, codigo: v.codigo });
      });
      m.aoClicarNoMapa((p) => {
        const { tracar: t, aoVertice: fn } = atual.current;
        if (t) fn({ latitude: p.lat, longitude: p.lng, codigo: null });
      });
      desligarTema = aoMudarTemaEfetivo((t) => m.definirTema(t));
      setControle(m);
    })().catch((e) => {
      console.error('análise: o motor do mapa não carregou', e);
      if (vivo) setSemMotor(true);
    });
    return () => {
      vivo = false;
      desligarTema?.();
      criado?.destruir();
    };
  }, []);

  /* As verticais: um registro por vertical, o lugar é o id (o clique devolve o lugar). */
  const chaveDasVerticais = verticais.map((v) => `${v.id}:${v.latitude}:${v.longitude}`).join('|');
  useEffect(() => {
    if (!controle) return;
    controle.definirRegistros(verticais.map((v): Feicao => ({
      chave: v.id, lugar: v.id, tipo: 'cptu', estado: 'exec', fonte: 'ponto', estimada: false,
      nome: v.codigo, cliente, latitude: v.latitude, longitude: v.longitude,
    })));
    controle.enquadrar();
    // A chave resume as verticais: o mesmo conjunto não reenquadra o mapa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controle, chaveDasVerticais, cliente]);

  /* As linhas: o conjunto inteiro a cada mudança. */
  useEffect(() => { controle?.definirLinhas(linhas); }, [controle, linhas]);

  const semMapa = falhas.has('webgl') || falhas.has('motor') || semMotor;
  useEffect(() => { aoSemMapa?.(semMapa); }, [semMapa, aoSemMapa]);

  return (
    <div className="cptu-analise-mapa">
      <div ref={caixa} className="mapa-caixa cptu-analise-mapa-caixa" />
      {semMapa && (
        <Aviso tipo="info" titulo="Este navegador não desenha o mapa (sem WebGL).">
          Escolha a linha pelas verticais, em «De» e «Até», embaixo.
        </Aviso>
      )}
    </div>
  );
}
